import {DEFAULT_RETRY_CONFIG, IDEMPOTENCY_HEADER_KEY, RETRY_ABLE_HTTP_STATUSES} from './HttpCustomRetry.constants';
import {HttpClientRetry, HttpClientRetryConfig, HttpRetryHeaders} from './HttpCustomRetry.types';

import {ErrorCode, HttpErrorProject, HttpErrorProjectResponse, HttpRetry, uuid4, wait} from '@specials/project-user';

type RetryParams<T> = {
  retryConfigData?: HttpClientRetry;
  makeHttpRequestCallback: (headers?: HttpRetryHeaders) => Promise<T>;
  onRetry?: (error?: HttpErrorProjectResponse) => void;
  signal?: AbortSignal;
};
type ConstructorProps = {
  errorCodes?: ErrorCode[];
};

export class HttpCustomRetry {
  private errorCodes: ErrorCode[] = [];

  constructor({errorCodes}: ConstructorProps) {
    if (errorCodes) {
      this.errorCodes = errorCodes;
    }
  }

  public static calculateTimeToWait(retries: number, delayInMs: number, timeBetweenRetryExponent: number): number {
    return Math.pow(retries * delayInMs, timeBetweenRetryExponent);
  }

  public async retry<T>({retryConfigData, makeHttpRequestCallback, onRetry, signal}: RetryParams<T>): Promise<T> {
    const retryConfig = this.getRetryConfig(retryConfigData);

    if (retryConfig === null) {
      return await makeHttpRequestCallback();
    }

    const idempotentId = this.generateIdempotentId();

    const isErrorCodeRetryAble = (errorCode: ErrorCode): boolean => {
      if (this.errorCodes.length !== 0) {
        return this.errorCodes.includes(errorCode);
      }

      return true;
    };

    async function retryWithBackOff(retries: number, retryConfig: HttpClientRetryConfig): Promise<T> {
      try {
        if (retries > 0) {
          const timeToWait = HttpRetry.calculateTimeToWait(
            retries,
            retryConfig.delayInMs,
            retryConfig.timeBetweenRetryExponent,
          );

          await wait(timeToWait);
        }
        return await makeHttpRequestCallback({[IDEMPOTENCY_HEADER_KEY]: idempotentId});
      } catch (error) {
        // Отменяем попытки retry если запрос остановлен AbortController.abort()
        if (signal?.aborted) {
          throw error;
        }
        if (
          retries < retryConfig.retryMaxLimit &&
          HttpErrorProject.isHttpErrorProject(error) &&
          RETRY_ABLE_HTTP_STATUSES.includes(error.httpCode) &&
          isErrorCodeRetryAble(error.errorCode)
        ) {
          onRetry?.(error);
          return retryWithBackOff(retries + 1, retryConfig);
        }

        throw error;
      }
    }

    return retryWithBackOff(0, retryConfig);
  }

  private getRetryConfig(retry?: HttpClientRetry): HttpClientRetryConfig | null {
    if (!retry) return null;
    return retry === true ? DEFAULT_RETRY_CONFIG : retry;
  }

  private generateIdempotentId(): string {
    return uuid4();
  }
}
